/**
 * InvoiceItemsTable Component
 * Fatura kalemlerini gösteren tablo component'i
 */

import React from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { InvoiceItem } from '../types/invoice.types';
import { formatQuantityForDisplay, getCurrencySymbol } from '../../utils/invoiceCalculations';
import { formatAmount, parseAmountInput } from '../../../../../utils/numberFormat';
import { InvoiceSection, SectionTitle } from '../styled/InvoiceStyledComponents';

interface InvoiceItemsTableProps {
  selectedItems: InvoiceItem[];
  currency: string;
  exchangeRate: string;
  loading: boolean;
  unitOptions: string[];
  onOpenItemModal: () => void;
  onAddManualItem: () => void;
  onOpenBulkWarehouseModal: () => void;
  onItemNameChange: (id: string, value: string) => void;
  onDisplayQuantityChange: (id: string, value: string) => void;
  onQuantityCommitOnBlur: (id: string) => void;
  onUnitChange: (id: string, value: string) => void;
  onEnteredPriceChange: (id: string, value: number) => void;
  onPriceTypeChange: (id: string, value: 'VAT_EXCLUDED' | 'VAT_INCLUDED') => void;
  onVatRateChange: (id: string, value: number) => void;
  onDiscountRateChange: (id: string, value: number) => void;
  /** İndirim giriş modu (kolon-seviyesi): tüm kalemler için ya yüzde ya tutar */
  discountMode: 'PERCENT' | 'AMOUNT';
  /** Kolon başlığındaki seçiciden mod değiştiğinde — tüm kalemlere uygulanır */
  onDiscountModeChange: (mode: 'PERCENT' | 'AMOUNT') => void;
  /** AMOUNT modunda satır indirim tutarı değişimi */
  onDiscountAmountChange: (id: string, value: number) => void;
  onDeleteItem: (id: string) => void;
  /** Mevcut belge tipi — 'Başka {Belge}den' butonunun etiketini belirler */
  mode?: 'invoice' | 'offer' | 'order' | 'waybill';
  /** Başka belgeden kalem aktarma modalını açan handler. Tanımlı değilse buton gizlenir. */
  onOpenCopyFromDocumentModal?: () => void;
}

export const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({
  selectedItems,
  currency,
  exchangeRate,
  loading,
  unitOptions,
  onOpenItemModal,
  onAddManualItem,
  onOpenBulkWarehouseModal,
  onItemNameChange,
  onDisplayQuantityChange,
  onQuantityCommitOnBlur,
  onUnitChange,
  onEnteredPriceChange,
  onPriceTypeChange,
  onVatRateChange,
  onDiscountRateChange,
  discountMode,
  onDiscountModeChange,
  onDiscountAmountChange,
  onDeleteItem,
  mode,
  onOpenCopyFromDocumentModal,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const docTrLabel: Record<NonNullable<InvoiceItemsTableProps['mode']>, string> = {
    invoice: 'Faturadan',
    offer: 'Teklifden',
    order: 'Siparişten',
    waybill: 'İrsaliyeden',
  };
  const docEnLabel: Record<NonNullable<InvoiceItemsTableProps['mode']>, string> = {
    invoice: 'Invoice',
    offer: 'Offer',
    order: 'Order',
    waybill: 'Waybill',
  };
  const copyButtonLabel = (() => {
    const m = mode || 'invoice';
    return tt(`Başka ${docTrLabel[m]}`, `From Another ${docEnLabel[m]}`);
  })();
  // Bolum basligi belge turune gore: Fatura/Teklif/Siparis/Irsaliye Kalemleri
  const docNounTr: Record<NonNullable<InvoiceItemsTableProps['mode']>, string> = {
    invoice: 'Fatura',
    offer: 'Teklif',
    order: 'Sipariş',
    waybill: 'İrsaliye',
  };
  const itemsTitle = (() => {
    const m = mode || 'invoice';
    return tt(`${docNounTr[m]} Kalemleri`, `${docEnLabel[m]} Items`);
  })();
  const unitLabelMap: Record<string, string> = {
    adet: 'piece',
    kg: 'kg',
    gr: 'g',
    lt: 'l',
    ml: 'ml',
    m: 'm',
    cm: 'cm',
    'm²': 'm²',
    'm³': 'm³',
    çift: 'pair',
    paket: 'pack',
    kutu: 'box',
    top: 'roll',
    rulo: 'roll',
  };

  const localizeUnit = (unit: string) => (isTr ? unit : (unitLabelMap[unit] || unit));

  const normalizeUnitInput = (input: string) => {
    if (isTr) return input;
    const normalized = input.trim().toLowerCase();
    const matched = Object.entries(unitLabelMap).find(([, enLabel]) => enLabel.toLowerCase() === normalized);
    return matched?.[0] || input;
  };

  return (
    <InvoiceSection>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <SectionTitle variant="h6" sx={{ fontSize: '1rem', mb: 0 }}>
          {itemsTitle}
        </SectionTitle>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={onOpenItemModal}
            disabled={loading}
            size="small"
            sx={{ mr: 1 }}
          >
            {tt('Ürün Ekle', 'Add Item')}
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<NotListedLocationIcon />}
            onClick={onAddManualItem}
            disabled={loading}
            size="small"
            sx={{ mr: 1 }}
          >
            {tt('Stoksuz Ürün Ekle', 'Add Non-Stock Item')}
          </Button>
          {onOpenCopyFromDocumentModal && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<ContentCopyIcon />}
              onClick={onOpenCopyFromDocumentModal}
              disabled={loading}
              size="small"
              sx={{ mr: 1 }}
            >
              {copyButtonLabel}
            </Button>
          )}
          <Button 
            variant="outlined" 
            color="info" 
            startIcon={<WarehouseIcon />}
            onClick={onOpenBulkWarehouseModal}
            disabled={loading || selectedItems.length === 0}
            size="small"
          >
            {tt('Toplu Depo Seç', 'Bulk Warehouse Select')}
          </Button>
        </Box>
      </Box>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', width: 40 }}>{tt('Sıra', 'No')}</TableCell>
              <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('Ürün', 'Item')}</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('Miktar', 'Quantity')}</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('Birim', 'Unit')}</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', bgcolor: 'warning.lighter', fontWeight: 'bold' }}>{tt('Fiyatı', 'Price')}</TableCell>
              <TableCell align="center" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>KDV</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>KDV %</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('KDV Hariç', 'Excl. VAT')}</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', bgcolor: 'success.lighter', fontWeight: 'bold' }}>{tt('KDV Dahil', 'Incl. VAT')}</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 0.5, fontSize: '0.75rem' }}>
                <FormControl size="small" variant="standard" sx={{ minWidth: 92 }}>
                  <Select
                    value={discountMode}
                    onChange={(e) => onDiscountModeChange(e.target.value as 'PERCENT' | 'AMOUNT')}
                    disableUnderline
                    sx={{ fontSize: '0.75rem', fontWeight: 'bold', '& .MuiSelect-select': { py: 0, pr: '20px !important' } }}
                  >
                    <MenuItem value="PERCENT" sx={{ fontSize: '0.75rem' }}>{tt('İndirim %', 'Discount %')}</MenuItem>
                    <MenuItem value="AMOUNT" sx={{ fontSize: '0.75rem' }}>{tt('İndirim ₺', 'Discount ₺')}</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', bgcolor: 'info.lighter', fontWeight: 'bold' }}>{tt('Tutar (KDV Dahil)', 'Amount (Incl. VAT)')}</TableCell>
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('KDV Tutarı', 'VAT Amount')}</TableCell>
              {currency !== 'TRY' && (
                <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>TRY</TableCell>
              )}
              <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>{tt('İşlem', 'Action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={currency !== 'TRY' ? 15 : 14} align="center" sx={{ fontSize: '0.75rem' }}>
                  {tt('Henüz ürün eklenmedi. Lütfen "Ürün Ekle" butonuna tıklayın.', 'No items added yet. Please click "Add Item".')}
                </TableCell>
              </TableRow>
            ) : (
              selectedItems.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell align="center" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 'bold', color: 'primary.main' }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {item.itemId.startsWith('manual_') ? (
                      <TextField
                        size="small"
                        value={item.itemName}
                        onChange={(e) => onItemNameChange(item.id, e.target.value)}
                        placeholder={tt('Ürün adı girin', 'Enter item name')}
                        inputProps={{ style: { fontSize: '0.75rem', padding: '2px 4px' } }}
                        sx={{ width: '100%', minWidth: 120 }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem' }}>{item.itemName}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    <TextField
                      type="text"
                      size="small"
                      value={item.displayQuantity || ''}
                      onChange={(e) => onDisplayQuantityChange(item.id, e.target.value)}
                      onBlur={() => onQuantityCommitOnBlur(item.id)}
                      onFocus={(e) => {
                        const target = e.target as HTMLInputElement;
                        const val = target.value;
                        setTimeout(() => {
                          target.setSelectionRange(val.length, val.length);
                        }, 0);
                      }}
                      onClick={(e) => {
                        const target = e.target as HTMLInputElement;
                        const val = target.value;
                        setTimeout(() => {
                          target.setSelectionRange(val.length, val.length);
                        }, 0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          onQuantityCommitOnBlur(item.id);
                        }
                      }}
                      inputMode="decimal"
                      sx={{ width: 70 }}
                      InputProps={{
                        inputProps: {
                          style: { 
                            fontSize: '0.75rem', 
                            padding: '2px 4px', 
                            textAlign: 'right'
                          }
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={unitOptions}
                      getOptionLabel={(option) => localizeUnit(option)}
                      renderOption={(props, option) => (
                        <li {...props}>{localizeUnit(option)}</li>
                      )}
                      value={item.unit}
                      onChange={(_e, newValue) => onUnitChange(item.id, normalizeUnitInput(newValue || ''))}
                      onInputChange={(_e, newValue) => onUnitChange(item.id, normalizeUnitInput(newValue || ''))}
                      disabled={!item.itemId.startsWith('manual_')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={tt('Birim', 'Unit')}
                          inputProps={{ ...params.inputProps, style: { fontSize: '0.75rem', padding: '2px 4px' } }}
                          sx={{ bgcolor: item.itemId.startsWith('manual_') ? 'background.paper' : 'action.disabledBackground' }}
                        />
                      )}
                      sx={{ width: 95 }}
                      disableClearable
                    />
                  </TableCell>
                  
                  {/* Fiyatı (enteredPrice) */}
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    <TextField
                      key={`price-${item.id}-${item.enteredPrice}`}
                      type="text"
                      size="small"
                      defaultValue={formatAmount(item.enteredPrice ?? item.unitPrice ?? 0)}
                      onBlur={(e) => {
                        const numValue = parseAmountInput(e.target.value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          onEnteredPriceChange(item.id, numValue);
                          e.target.value = formatAmount(numValue);
                        } else {
                          e.target.value = formatAmount(item.enteredPrice ?? item.unitPrice ?? 0);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      inputMode="decimal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ ml: 0.5 }}>
                            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                              {getCurrencySymbol(currency)}
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                      inputProps={{ style: { fontSize: '0.75rem', padding: '2px 4px', textAlign: 'right' } }}
                      sx={{ width: 95, bgcolor: 'warning.lighter' }}
                      onClick={(e) => {
                        const input = e.currentTarget.querySelector('input');
                        if (input) input.select();
                      }}
                      onFocus={(e) => {
                        const input = e.currentTarget as HTMLInputElement;
                        if (input) input.select();
                      }}
                    />
                  </TableCell>
                  
                  {/* KDV Dropdown (priceType) */}
                  <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                    <FormControl size="small" sx={{ width: 100 }}>
                      <Select
                        value={item.priceType}
                        onChange={(e) => onPriceTypeChange(item.id, e.target.value as 'VAT_EXCLUDED' | 'VAT_INCLUDED')}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: '28px' }}
                      >
                        <MenuItem value="VAT_EXCLUDED" sx={{ fontSize: '0.75rem' }}>{tt('Hariç', 'Excluded')}</MenuItem>
                        <MenuItem value="VAT_INCLUDED" sx={{ fontSize: '0.75rem' }}>{tt('Dahil', 'Included')}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  
                  {/* KDV % */}
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={item.vatRate}
                      onChange={(e) => onVatRateChange(item.id, parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 100, step: 1, style: { fontSize: '0.75rem', padding: '2px 4px', textAlign: 'right' } }}
                      sx={{ width: 50 }}
                      onClick={(e) => {
                        const input = e.currentTarget.querySelector('input');
                        if (input) input.select();
                      }}
                      onFocus={(e) => {
                        const input = e.currentTarget as HTMLInputElement;
                        if (input) input.select();
                      }}
                    />
                  </TableCell>
                  
                  {/* KDV Hariç */}
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {formatAmount(item.unitPrice)} {getCurrencySymbol(currency)}
                    </Typography>
                  </TableCell>
                  
                  {/* KDV Dahil */}
                  <TableCell align="right" sx={{ py: 0.5, px: 1, bgcolor: 'success.lighter' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'success.dark' }}>
                      {formatAmount(item.unitPriceWithVat)} {getCurrencySymbol(currency)}
                    </Typography>
                  </TableCell>
                  
                  {/* İndirim (kolon moduna göre % veya ₺) */}
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    {discountMode === 'AMOUNT' ? (
                      <TextField
                        key={`disc-amt-${item.id}-${item.discountAmount}`}
                        type="text"
                        size="small"
                        defaultValue={formatAmount(item.discountAmount ?? 0)}
                        onBlur={(e) => {
                          const numValue = parseAmountInput(e.target.value);
                          if (!isNaN(numValue) && numValue >= 0) {
                            onDiscountAmountChange(item.id, numValue);
                            e.target.value = formatAmount(numValue);
                          } else {
                            e.target.value = formatAmount(item.discountAmount ?? 0);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        inputMode="decimal"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ ml: 0 }}>
                              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                {getCurrencySymbol(currency)}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ style: { fontSize: '0.75rem', padding: '2px 4px', textAlign: 'right' } }}
                        sx={{ width: 80 }}
                        onClick={(e) => {
                          const input = e.currentTarget.querySelector('input');
                          if (input) input.select();
                        }}
                        onFocus={(e) => {
                          const input = e.currentTarget as HTMLInputElement;
                          if (input) input.select();
                        }}
                      />
                    ) : (
                      <TextField
                        type="number"
                        size="small"
                        value={item.discountRate}
                        onChange={(e) => onDiscountRateChange(item.id, parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, max: 100, step: 1, style: { fontSize: '0.75rem', padding: '2px 4px', textAlign: 'right' } }}
                        sx={{ width: 50 }}
                        onClick={(e) => {
                          const input = e.currentTarget.querySelector('input');
                          if (input) input.select();
                        }}
                        onFocus={(e) => {
                          const input = e.currentTarget as HTMLInputElement;
                          if (input) input.select();
                        }}
                      />
                    )}
                  </TableCell>
                  
                  {/* Tutar (KDV Dahil) */}
                  <TableCell align="right" sx={{ py: 0.5, px: 1, bgcolor: 'info.lighter' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'info.dark' }}>
                      {formatAmount(item.grossTotal)} {getCurrencySymbol(currency)}
                    </Typography>
                  </TableCell>
                  
                  {/* KDV Tutarı */}
                  <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem', color: 'success.main', fontWeight: 'medium' }}>
                    {formatAmount(item.vatAmount)} {getCurrencySymbol(currency)}
                  </TableCell>
                  
                  {currency !== 'TRY' && (
                    <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(item.grossTotal * parseFloat(exchangeRate))}
                    </TableCell>
                  )}
                  
                  <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => onDeleteItem(item.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </InvoiceSection>
  );
};
